import type {
  ServerProject,
  ServerSite,
  ServerSiteSummary,
  ServerSiteWorkbookSummary,
  ServerWorkbook,
} from '../../data/ServerApiClient'

/**
 * Pure helpers for the CFO experiences selector. Kept separate from the React
 * component so the filtering / sorting / site-resolution logic can be unit
 * tested without a DOM.
 */

const parseTime = (value: string | null | undefined): number => {
  if (!value) return 0
  const t = Date.parse(value)
  return Number.isNaN(t) ? 0 : t
}

/**
 * The workbooks that represent a CFO Core experience. Use the durable
 * provider-neutral accounting binding signal instead of guessing from workbook
 * kind; the QuickBooks-named boolean is a compatibility alias for one release.
 * Drops anything missing a
 * `site_slug` (needed to build the `/cfo/home` route). Sorted most-recently
 * viewed first.
 */
export function selectCfoExperiences(workbooks: ServerWorkbook[]): ServerWorkbook[] {
  return workbooks
    .filter((w) => (!!w.accounting_provider || w.has_quickbooks_connection === true) && !!w.site_slug)
    .slice()
    .sort((a, b) => {
      const byViewed = parseTime(b.last_viewed_at) - parseTime(a.last_viewed_at)
      if (byViewed !== 0) return byViewed
      const byUpdated = parseTime(b.updated_at) - parseTime(a.updated_at)
      if (byUpdated !== 0) return byUpdated
      return a.name.localeCompare(b.name)
    })
}

/**
 * While the release hides sites/workbooks, old site collection URLs are only an
 * entry point to the global CFO selector. Normalize them to `/` so the address
 * bar does not imply the page is scoped to one site.
 */
export function cfoSelectorCanonicalPath(pathname: string, search = ''): string | null {
  const params = new URLSearchParams(search)
  if (params.get('view') === 'tableau-creator') return null
  const parts = String(pathname || '').split('/').filter(Boolean)
  if (parts.length === 2 && parts[1] === 'workbooks') return '/'
  return null
}

const siteRoleIsAdmin = (role: string | null | undefined): boolean => {
  const normalized = String(role || '').trim().toLowerCase().replace(/-/g, '_')
  return normalized === 'site_admin_creator' || normalized === 'site_admin_explorer' || normalized === 'admin'
}

export function cfoSiteCanManageProjects(site: Pick<ServerSite, 'current_site_role'> | null | undefined): boolean {
  return siteRoleIsAdmin(site?.current_site_role)
}

export function cfoSiteCanManageUsers(site: Pick<ServerSite, 'current_site_role'> | null | undefined): boolean {
  return siteRoleIsAdmin(site?.current_site_role)
}

export function cfoSiteCanAddExperience(site: Pick<ServerSite, 'current_site_role'> | null | undefined): boolean {
  const normalized = String(site?.current_site_role || '').trim().toLowerCase().replace(/-/g, '_')
  return ['site_admin_creator', 'site_admin_explorer', 'admin', 'creator', 'explorer_can_publish'].includes(normalized)
}

export function resolveCfoAdminToolsPath(
  tenantAdminSites: ServerSite[] | null | undefined,
  mySites: ServerSite[] | null | undefined,
): string | null {
  if (Array.isArray(tenantAdminSites)) return '/admin/sites'
  const site = (mySites || []).find((s) => cfoSiteCanManageProjects(s) && !!s.slug)
  return site?.slug ? `/${encodeURIComponent(site.slug)}/home` : null
}

export type CfoProjectNode = {
  project: ServerProject
  workbooks: ServerSiteWorkbookSummary[]
  children: CfoProjectNode[]
}

export type CfoSiteProjectSection = {
  site: ServerSite
  projects: CfoProjectNode[]
  unassignedWorkbooks: ServerSiteWorkbookSummary[]
  workbookCount: number
}

/**
 * A CFO Core made for its QuickBooks admin to connect through a standing link,
 * which nobody has connected yet. It has no books to open. It is listed anyway
 * (shown as waiting, or with its link revoked) so its link can be shared again,
 * replaced, or the CFO Core deleted.
 */
export function cfoWorkbookAwaitingQuickBooksAdmin(
  workbook: Pick<ServerSiteWorkbookSummary, 'accounting_provider' | 'has_quickbooks_connection' | 'qbo_connect_link_status'>,
): boolean {
  return !workbook.accounting_provider
    && workbook.has_quickbooks_connection !== true
    && (workbook.qbo_connect_link_status === 'active' || workbook.qbo_connect_link_status === 'revoked')
}

const workbookIsCfoExperience = (workbook: ServerSiteWorkbookSummary): boolean =>
  !!workbook.accounting_provider
  || workbook.has_quickbooks_connection === true
  || cfoWorkbookAwaitingQuickBooksAdmin(workbook)

const nodeHasCfoWork = (node: CfoProjectNode): boolean =>
  node.workbooks.length > 0 || node.children.some(nodeHasCfoWork)

const pruneProjectNode = (node: CfoProjectNode): CfoProjectNode | null => {
  const children = node.children.map(pruneProjectNode).filter((child): child is CfoProjectNode => !!child)
  if (node.workbooks.length === 0 && children.length === 0) return null
  return { ...node, children }
}

const projectIsDefault = (project: Pick<ServerProject, 'name' | 'slug'>): boolean => {
  const name = String(project.name || '').trim().toLowerCase()
  const slug = String(project.slug || '').trim().toLowerCase()
  return name === 'default' || slug === 'default'
}

const projectNodeUsesExplicitProject = (node: CfoProjectNode): boolean =>
  !projectIsDefault(node.project) || node.children.some(projectNodeUsesExplicitProject)

export function cfoSiteUsesProjects(section: CfoSiteProjectSection): boolean {
  return section.projects.some(projectNodeUsesExplicitProject)
}

export function cfoSiteProjectWorkbooks(section: CfoSiteProjectSection): ServerSiteWorkbookSummary[] {
  const out: ServerSiteWorkbookSummary[] = []
  for (const project of section.projects) {
    appendProjectWorkbooks(project, out)
  }
  out.push(...section.unassignedWorkbooks)
  return out
}

export function selectCfoSiteProjectSections(summaries: ServerSiteSummary[]): CfoSiteProjectSection[] {
  const sections: CfoSiteProjectSection[] = []
  for (const summary of summaries) {
    const nodeByProjectId = new Map<string, CfoProjectNode>()
    const rootProjects: CfoProjectNode[] = []
    const unassignedWorkbooks: ServerSiteWorkbookSummary[] = []

    for (const project of summary.projects || []) {
      nodeByProjectId.set(project.id, { project, workbooks: [], children: [] })
    }

    for (const project of summary.projects || []) {
      const node = nodeByProjectId.get(project.id)
      if (!node) continue
      const parentId = String(project.parent_project_id || '').trim()
      const parent = parentId ? nodeByProjectId.get(parentId) : null
      if (parent) parent.children.push(node)
      else rootProjects.push(node)
    }

    for (const workbook of summary.workbooks || []) {
      if (!workbookIsCfoExperience(workbook)) continue
      const projectNode = nodeByProjectId.get(workbook.project_id)
      if (projectNode) projectNode.workbooks.push(workbook)
      else unassignedWorkbooks.push(workbook)
    }

    const projects = rootProjects
      .filter(nodeHasCfoWork)
      .map(pruneProjectNode)
      .filter((node): node is CfoProjectNode => !!node)
    const workbookCount =
      unassignedWorkbooks.length +
      projects.reduce((total, project) => total + countProjectWorkbooks(project), 0)
    if (workbookCount > 0) {
      sections.push({
        site: summary.site,
        projects,
        unassignedWorkbooks,
        workbookCount,
      })
    }
  }
  return sections
}

function countProjectWorkbooks(node: CfoProjectNode): number {
  return node.workbooks.length + node.children.reduce((total, child) => total + countProjectWorkbooks(child), 0)
}

function appendProjectWorkbooks(node: CfoProjectNode, out: ServerSiteWorkbookSummary[]): void {
  out.push(...node.workbooks)
  for (const child of node.children) appendProjectWorkbooks(child, out)
}
